import React, { PureComponent } from "react";
import styled, {css} from "styled-components";
import { darken } from "polished";
import { cssMainSelector } from "../lib/cssSelectors";

const buttonBasePadding = css `padding: ${p => p.theme.spacing.smm} ${p => p.theme.spacing.lg}`;
const ButtonBase = styled.button`
  ${p => cssMainSelector(p.theme.tdsId)} {
    appearance: none;
    border-radius: ${p => p.theme.generic.borderRadius};
    border-width: ${p => p.theme.button.borderWidth};
    border-style: ${p => p.theme.button.borderStyle};
    cursor: pointer;
    display: inline-block;
    font-family: ${p => p.theme.font.family};
    font-size: 1em;
    font-weight: ${p => p.theme.font.weightNormal};
    outline: 0;
    ${buttonBasePadding};
    text-align: center;
    text-decoration: none;
    user-select: none;
    vertical-align: middle;
    white-space: nowrap;
    ${p => p.disabled && "cursor: not-allowed"};
    ${p => p.disabled && "opacity: 0.5"};
  }
`;

const PrimaryButton = styled(ButtonBase)`
  ${p => cssMainSelector(p.theme.tdsId)} {
    background-color: ${p => p.theme.primaryButton.bgColor};
    box-shadow: ${p => p.theme.primaryButton.boxShadow};
    border-color: ${p => p.theme.primaryButton.borderColor};
    color: ${p => p.theme.primaryButton.textColor};
    font-weight: ${p => p.theme.font.weightBold};
  
    * {
      color: ${p => p.theme.primaryButton.textColor};
    }
  
    &:hover,
    &:focus {
      background-color: ${p => darken(0.1, p.theme.primaryButton.bgColor)};
      color: ${p => p.theme.primaryButton.textColor};
    }
  }
`;

const PrimaryInverseButton = styled(ButtonBase)`
  ${p => cssMainSelector(p.theme.tdsId)} {
    background-color: ${p => p.theme.primaryInverseButton.bgColor};
    box-shadow: ${p => p.theme.primaryInverseButton.boxShadow};
    border-color: ${p => p.theme.primaryInverseButton.borderColor};
    color: ${p => p.theme.primaryInverseButton.textColor};
    font-weight: ${p => p.theme.font.weightBold};
  
    * {
      color: ${p => p.theme.primaryButton.bgColor};
    }
  
    &:hover,
    &:focus {
      background-color: ${p => p.theme.color.aliceBlue};
      color: ${p => p.theme.color.cornFlowerBlue};
    }
  }
`;

const ClickOutButton = styled(ButtonBase)`
  ${p => cssMainSelector(p.theme.tdsId)} {
    background-color: ${p => p.theme.clickOutButton.bgColor};
    border-color: ${p => p.theme.clickOutButton.borderColor};
    border-radius: ${p => p.theme.clickOutButton.borderRadius};
    border-style: ${p => p.theme.clickOutButton.borderStyle};
    border-width: ${p => p.theme.clickOutButton.borderWidth};
    box-shadow: ${p => p.theme.clickOutButton.boxShadow};
    color: ${p => p.theme.clickOutButton.textColor};
    font-weight: ${p => p.theme.font.weightBold};
  
    * {
      color: ${p => p.theme.clickOutButton.textColor};
    }
  
    &:hover,
    &:focus {
      background-color: ${p => p.theme.clickOutButton.hoverBackgroundColor};
      color: ${p => p.theme.clickOutButton.hoverTextColor};
    }
  }
`;

// We would like to use something like:
//
// const AnchorButtonBase = ButtonBase.withComponent("a");
//
// The problem this gives us is specificity in CSS. If some client styles
// #clientOutSideClass a then this has more specificity than our class on the
// a element, so we need to style the a element from a class surrounding the a element
const AnchorButtonBaseWrapper = styled.span`
  ${p => cssMainSelector(p.theme.tdsId)} {
    a,
    a:active,
    a:hover,
    a:link,
    a:visited {
      text-decoration: none;
    }
  }
`;

const AnchorPrimaryButtonWrapper = styled.span`
  ${p => cssMainSelector(p.theme.tdsId)} {
    a,
    a:active,
    a:hover,
    a:link,
    a:visited {
      color: ${p => p.theme.color.white};
      text-decoration: none;
    }
  }
`;

const AnchorPrimaryInverseButtonWrapper = styled.span`
  ${p => cssMainSelector(p.theme.tdsId)} {
    a,
    a:active,
    a:hover,
    a:link,
    a:visited {
      color: ${p => p.theme.color.cornFlowerBlue};
      text-decoration: none;
    }
  }
`;

const AnchorClickOutButtonWrapper = styled.span`
  ${p => cssMainSelector(p.theme.tdsId)} {
    a,
    a:active,
    a:hover,
    a:link,
    a:visited {
      color: ${p => p.theme.clickOutButton.textColor};
      font-weight: ${p => p.theme.font.weightBold};
      text-decoration: none;
    }
  }
`;

class AnchorButtonBase extends PureComponent {
  render() {
    return (
      <AnchorButtonBaseWrapper>
        <a {...this.props}>
          <ButtonBase>{this.props.children}</ButtonBase>
        </a>
      </AnchorButtonBaseWrapper>
    );
  }
}

class AnchorPrimaryButton extends PureComponent {
  render() {
    return (
      <AnchorPrimaryButtonWrapper>
        <a {...this.props}>
          <PrimaryButton>{this.props.children}</PrimaryButton>
        </a>
      </AnchorPrimaryButtonWrapper>
    );
  }
}

class AnchorPrimaryInverseButton extends PureComponent {
  render() {
    return (
      <AnchorPrimaryInverseButtonWrapper>
        <a {...this.props}>
          <PrimaryInverseButton>{this.props.children}</PrimaryInverseButton>
        </a>
      </AnchorPrimaryInverseButtonWrapper>
    );
  }
}

class AnchorClickOutButton extends PureComponent {
  render() {
    return (
      <AnchorClickOutButtonWrapper>
        <a {...this.props}>
          <ClickOutButton>{this.props.children}</ClickOutButton>
        </a>
      </AnchorClickOutButtonWrapper>
    );
  }
}

export {
  AnchorButtonBase,
  AnchorClickOutButton,
  AnchorPrimaryButton,
  AnchorPrimaryInverseButton,
  ButtonBase,
  ClickOutButton,
  PrimaryButton,
  PrimaryInverseButton
};
